"use client";

import React from "react";

import { Box, Grid, GridColumn, Icon, Target } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { Overlay } from "../Overlay";

interface SidePanelProps {
  children: React.ReactNode;
  title?: string;
  position?: "start" | "end";
  visible: boolean;
  onDismiss: () => void;
}

export const SidePanel: React.FC<SidePanelProps> = (props: SidePanelProps) => {
  const {
    children,
    position = "end",
    title,
    visible: visibleProp,
    onDismiss
  } = props;
  const [, setForceUpdate] = React.useState(0);
  const [hasTransitionedOut, setHasTransitionedOut] = React.useState(true);
  const [visible, visibleSet] = React.useState(visibleProp);
  const visiblePropPrev = React.useRef(visibleProp);
  const transitionDelayTimeout = React.useRef<ReturnType<typeof setTimeout>>();

  const handleTransitionEnd = React.useCallback(
    (e: React.TransitionEvent) => {
      if (!visible && e.target === e.currentTarget) {
        setHasTransitionedOut(true);
      }
    },
    [visible]
  );

  React.useEffect(() => {
    // Force a render on mount so the element exists and
    // transitions start working
    setForceUpdate(1);
  }, []);

  React.useEffect(() => {
    if (visible && hasTransitionedOut) {
      setHasTransitionedOut(false);
    }
  }, [hasTransitionedOut, visible]);

  React.useEffect(() => {
    const isShowing = visiblePropPrev.current;

    clearTimeout(transitionDelayTimeout.current);

    // If it's showing, wait a moment so the animation in can be seen
    if (isShowing) {
      transitionDelayTimeout.current = setTimeout(() => {
        visibleSet(visibleProp);
      }, 100);
    } else {
      visibleSet(visibleProp);
    }

    return () => {
      clearTimeout(transitionDelayTimeout.current);
    };
  }, [visibleProp]);

  visiblePropPrev.current = visibleProp;

  return (
    <Overlay visible={visibleProp} onDismiss={onDismiss}>
      <Box
        aria-hidden={!visible}
        data-testid="side-panel"
        sx={{
          height: 0,
          left: 0,
          overflow: "visible",
          position: "fixed",
          top: 0,
          width: "100%"
        }}
      >
        <Grid
          initial={true}
          sx={{
            height: 0,
            overflow: "visible"
          }}
          variant="navigation"
        >
          <GridColumn
            fillEnd={position === "end"}
            fillGapEnd={position === "end"}
            fillGapStart={true}
            fillStart={position === "start"}
            onTransitionEnd={handleTransitionEnd}
            push={position === "end" ? [0, 4, 8] : [0, 0, 0]}
            size={[4, 4, 4]}
            sx={{
              bg: "surface-neutral-default",
              boxShadow: "shadow-100",
              height: hasTransitionedOut ? 0 : "100vh",
              opacity: !visible ? 0 : 1,
              transition: [
                "transition-ease-out-medium",
                null,
                "transition-ease-out-fast"
              ],
              transform: !visible
                ? `translateX(${position === "end" ? "" : "-"}150px)`
                : "none"
            }}
          >
            {hasTransitionedOut ? null : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  maxHeight: "100vh",
                  pt: "spacing-5",
                  px: "spacing-5"
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: title ? "space-between" : "end",
                    pb: "spacing-3",
                    width: "100%"
                  }}
                >
                  {title && <Text variant="title-2-medium">{title}</Text>}
                  <Target
                    autoFocus={true}
                    data-testid="side-panel-dismiss"
                    onClick={onDismiss}
                    aria-label="close side panel"
                  >
                    <Icon icon="x" size="sm" color="control-enabled" />
                  </Target>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch"
                  }}
                >
                  {children}
                </Box>
              </Box>
            )}
          </GridColumn>
        </Grid>
      </Box>
    </Overlay>
  );
};
